/**
 * PharmacyDemoBanner.tsx — Top Strip for Pharmacy Partner Demo
 *
 * Replaces DemoBanner when isPharmacyDemo() is true.
 * Fixed to the TOP of the screen (not bottom — pharmacy audience
 * is viewing on desktop or held device, not thumb-scrolling).
 *
 * Contains:
 *   - Partner name + demo label
 *   - Core / Trak tier toggle
 *   - "Why pharmacies use CoreTrak" button (opens PharmacyValueScreen)
 *
 * Does NOT contain a "Sign up" CTA — pharmacy demos end with
 * PharmacyValueScreen which has the partner contact CTA.
 *
 * Place at: src/demo/PharmacyDemoBanner.tsx
 */

import { useState, useCallback, useEffect } from 'react';
import { getPharmacyConfig } from './demoPartner';
import { usePharmacyDemoTier } from './demoPharmacyMode';
import { trackDemoEvent } from './demoAnalytics';

export function PharmacyDemoBanner() {
  const config = getPharmacyConfig();
  const { tier, isCore, toggleTier } = usePharmacyDemoTier();

  const DISMISS_KEY = 'coretrak.demo.pharmacyBannerDismissed';
  const [bottomDismissed, setBottomDismissed] = useState(() => {
    try { return sessionStorage.getItem(DISMISS_KEY) === '1'; } catch { return false; }
  });

  // Reappear on navigation — reset dismiss state when location changes
  useEffect(() => {
    setBottomDismissed(false);
    try { sessionStorage.removeItem(DISMISS_KEY); } catch { /* ignore */ }
  }, [window.location.pathname]);

  const handleDismissBottom = useCallback(() => {
    setBottomDismissed(true);
    try { sessionStorage.setItem(DISMISS_KEY, '1'); } catch { /* ignore */ }
  }, []);

  const handleToggle = useCallback(() => {
    const next = isCore ? 'trak' : 'core';
    trackDemoEvent('pharmacy_demo_tier_toggle', { from: tier, to: next });
    toggleTier();
  }, [isCore, tier, toggleTier]);

  if (!config) return null;

  return (
    <>
      {/* ── Top banner strip ── */}
      <div
        style={{
          position: 'fixed',
          top: 0,
          left: 0,
          right: 0,
          zIndex: 200,
          background: '#0f1729',
          borderBottom: '1px solid rgba(0,168,181,0.25)',
          padding: '0 16px',
          height: 48,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 12,
        }}
      >
        {/* Left — MLP logo */}
        <div style={{ display: 'flex', alignItems: 'center', flexShrink: 0 }}>
          <img src="https://images.squarespace-cdn.com/content/v1/66b5cd025a58fd45b2d48108/16f832c3-fd97-4df6-93c5-2348e6734897/logi.png" alt="MyLondonPharmacy" style={{ height: 32, width: 'auto', objectFit: 'contain', filter: 'brightness(0) invert(1)', opacity: 0.9 }} />
        </div>

        {/* Centre — Core / Trak tier toggle pill */}
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            background: 'rgba(255,255,255,0.06)',
            borderRadius: 20,
            padding: 3,
            gap: 2,
            position: 'absolute' as const,
            left: '50%',
            transform: 'translateX(-50%)',
          }}
        >
          <button
            type="button"
            onClick={isCore ? undefined : handleToggle}
            style={{
              background: isCore ? '#007C86' : 'transparent',
              color: isCore ? '#fff' : 'rgba(255,255,255,0.4)',
              border: 'none',
              borderRadius: 16,
              padding: '4px 14px',
              fontSize: 11,
              fontWeight: 700,
              cursor: isCore ? 'default' : 'pointer',
              transition: '0.2s',
              fontFamily: 'inherit',
              letterSpacing: 0.2,
            }}
          >
            Core
          </button>
          <button
            type="button"
            onClick={!isCore ? undefined : handleToggle}
            style={{
              background: !isCore ? '#007C86' : 'transparent',
              color: !isCore ? '#fff' : 'rgba(255,255,255,0.4)',
              border: 'none',
              borderRadius: 16,
              padding: '4px 14px',
              fontSize: 11,
              fontWeight: 700,
              cursor: !isCore ? 'default' : 'pointer',
              transition: '0.2s',
              fontFamily: 'inherit',
              letterSpacing: 0.2,
            }}
          >
            Trak
          </button>
        </div>

                {/* Right — CoreTrak logo in white pill */}
        <div style={{
          display: 'flex',
          alignItems: 'center',
          flexShrink: 0,
          background: '#fff',
          borderRadius: 20,
          padding: '4px 10px',
        }}>
          <img src="data:image/svg+xml;base64,PHN2ZyBpZD0iTGF5ZXJfMSIgZGF0YS1uYW1lPSJMYXllciAxIiB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCA5MDAgNjg2Ij48ZGVmcz48c3R5bGU+LmNscy0xe2ZpbGw6IzE1MTUxNTt9LmNscy0ye2ZpbGw6IzAwN2M4Njt9PC9zdHlsZT48L2RlZnM+PHBhdGggY2xhc3M9ImNscy0xIiBkPSJNNDc0LjQzLDQ3OS45NFYyMzEuMzdINTg3LjExVjE4MS43OUgyNzUuNTdhMjAwLjU0LDIwMC41NCwwLDAsMSwzMjcuMTItMzFsNDMuNjItMjYuNUEyNTAuNTEsMjUwLjUxLDAsMCwwLDQ1MCwyOS43NWMtMTAzLjUsMC0xOTIuMzQsNjIuNjItMjMwLjczLDE1MmEyNDMuNjgsMjQzLjY4LDAsMCwwLTE1LjQ1LDQ5LjU4LDI1Mi45MSwyNTIuOTEsMCwwLDAtNC44OCw0OS40OGMwLDEzMC4yMSw5OS4xLDIzNy4yNSwyMjUuOTQsMjQ5Ljg2QTI0OC4xMiwyNDguMTIsMCwwLDAsNDUwLDUzMmM4LjI1LDAsMTYuNDItLjQsMjQuNDUtMS4yYTI1MC4zOCwyNTAuMzgsMCwwLDAsMTcwLjY0LTkxLjgyTDYwMS40LDQxMi4zOUEyMDAuMTEsMjAwLjExLDAsMCwxLDQ3NC40Myw0NzkuOTRabS00OS41Ny0uMDljLTk4LjkyLTEyLjM0LTE3NS40My05Ni43NS0xNzUuNDMtMTk5YTIwMi4xOSwyMDIuMTksMCwwLDEsNi4xMi00OS40OEg0MjQuODZaIi8+PHBhdGggY2xhc3M9ImNscy0yIiBkPSJNNzAxLjA4LDI4MC44NUEyNTAsMjUwLDAsMCwxLDY2Mi42LDQxNC41MmwtNDMuMjItMjYuMjNhMjAwLjczLDIwMC43MywwLDAsMCwxLjA2LTIxMy4yNGw0My4xOC0yNi4xOUEyNTAsMjUwLDAsMCwxLDcwMS4wOCwyODAuODVaIi8+PHBhdGggY2xhc3M9ImNscy0xIiBkPSJNODQuNjQsNjM0LjY2bDkuODksMTBRODMsNjU2LjA5LDY1LjMxLDY1Ni4wOEE0Ny4xNCw0Ny4xNCwwLDAsMSw0Nyw2NTIuNzNhMzIuOTQsMzIuOTQsMCwwLDEtMTIuOC05QTM5LjM4LDM5LjM4LDAsMCwxLDI3LDYzMS4xNGE0NC4zNCw0NC4zNCwwLDAsMS0yLjQxLTE0LjcyQTQ1LDQ1LDAsMCwxLDI3LDYwMS41OWE0MC43LDQwLjcsMCwwLDEsNy4zNi0xMywzNC4xMSwzNC4xMSwwLDAsMSwxMi45MS05LjI5LDQ1LDQ1LDAsMCwxLDE4LTMuNDZxMTYuMjYsMCwyOC4yMywxMS43Nkw4My44Nyw1OTdxLTcuMzUtNy4yNi0xOC41Ni03LjI2LTEzLDAtMTkuNzgsOGEyOS4wNywyOS4wNywwLDAsMCwwLDM3LjEzcTYuNiw3LjY1LDE5Ljc4LDcuNjRhMjguNTQsMjguNTQsMCwwLDAsMTAuMzgtMkEyNS43MSwyNS43MSwwLDAsMCw4NC42NCw2MzQuNjZaIi8+PHBhdGggY2xhc3M9ImNscy0xIiBkPSJNMTQwLjM1LDU3NS44OGE0Mi40NCw0Mi40NCwwLDAsMSwxNy4xNCwzLjU3LDMzLjU5LDMzLjU5LDAsMCwxLDEyLjQxLDkuMjMsNDIsNDIsMCwwLDEsNy4yLDEyLjg2LDQyLjksNDIuOSwwLDAsMSwyLjM2LDE0Ljc3QTQ2LjQ5LDQ2LjQ5LDAsMCwxLDE3Ni45Myw2MzFhNDAuNTIsNDAuNTIsMCwwLDEtNy4wOCwxMi42MywzMywzMywwLDAsMS0xMi4zNiw5LjA3LDQyLjc0LDQyLjc0LDAsMCwxLTE3LjQ3LDMuNCw0NC4zNyw0NC4zNywwLDAsMS0xNy41My0zLjI5LDMyLjQyLDMyLjQyLDAsMCwxLTEyLjQ3LTksMzkuODksMzkuODksMCwwLDEtNy4xNC0xMi42Myw0NC44Nyw0NC44NywwLDAsMS0yLjQyLTE0Ljc4cTAtMTcuOTEsMTEuNDMtMjkuMjJUMTQwLjM1LDU3NS44OFptLTI1LjYsNDAuNzZhMzAuMDUsMzAuMDUsMCwwLDAsNi41MywxOC40NnE2LjIxLDcuNywxOC43NCw3LjY5VDE1OC43NSw2MzVhMjkuNywyOS43LDAsMCwwLDYuNDMtMTguNTEsMjkuMjMsMjkuMjMsMCwwLDAtNi4xNi0xOXEtNi4zNy04LjI0LTE4LjY3LTguNDUtMTIsMC0xOSw3LjkxVDExNC43NSw2MTYuNjRaIi8+PHBhdGggY2xhc3M9ImNscy0xIiBkPSJNMjYyLjMsNjUzLjM0djFIMjQ1LjcxTDIyMy40LDYyOC42MkgyMDguNjh2MjUuNzFoLTE0LjV2LTc3cTYsMCwxOC4yNC4wNXQxOC4zNS4wNnExMywuMTEsMjAuMzIsNy41M2EyNC41NCwyNC41NCwwLDAsMSw3LjM2LDE4LDI2LjQxLDI2LjQxLDAsMCwxLTQuNjEsMTUuNDNRMjQ5LjIyLDYyNSwyMzksNjI3LjA4Wm0tMzEuNTMtNjIuNDFIMjA4LjY4djI0LjYxaDIyLjA5cTYuNDgsMCw5LjgzLTMuNjJhMTIuODQsMTIuODQsMCwwLDAtLjA2LTE3LjM2UTIzNy4xNCw1OTAuOTMsMjMwLjc3LDU5MC45M1oiLz48cGF0aCBjbGFzcz0iY2xzLTEiIGQ9Ik0zMzIuODMsNjQwdjE0LjI5SDI3NS4yNlY1NzcuNDJoNTcuNTd2MTQuMDZIMjg5LjY1djE3LjY5aDQxLjY0djEzLjUxSDI4OS42NVY2NDBaIi8+PHBhdGggY2xhc3M9ImNscy0yIiBkPSJNMzY2LjU2LDY1NC4zM1Y1OTAuNkgzNDIuMTdWNTc3LjQyaDYzLjI4VjU5MC42SDM4MS4wNnY2My43M1oiLz48cGF0aCBjbGFzcz0iY2xzLTIiIGQ9Ik00ODUuNjUsNjUzLjM0djFINDY5LjA2bC0yMi4zLTI1LjcxSDQzMnYyNS43MWgtMTQuNXYtNzdxNiwwLDE4LjIzLjA1dDE4LjM1LjA2cTEzLC4xMSwyMC4zMyw3LjUzYTI0LjU3LDI0LjU3LDAsMCwxLDcuMzYsMTgsMjYuMzUsMjYuMzUsMCwwLDEtNC42MiwxNS40M3EtNC42LDYuNjYtMTQuODMsOC43NFptLTMxLjUzLTYyLjQxSDQzMnYyNC42MWgyMi4wOHE2LjQ4LDAsOS44My0zLjYyYTEyLjg1LDEyLjg1LDAsMCwwLS4wNS0xNy4zNlE0NjAuNSw1OTAuOTMsNDU0LjEyLDU5MC45M1oiLz48cGF0aCBjbGFzcz0iY2xzLTIiIGQ9Ik01NTguODIsNjU0LjMzbC02LjQ4LTE0LjUxSDUxMi4xM2wtNi41OSwxNC41MUg0ODkuODNsMzQuNS03Ni45MWgxNS44MmwzNC40OSw3Ni45MVptLTEyLjMtMjgtMTQuMjgtMzIuNzRMNTE4LDYyNi4zMVoiLz48cGF0aCBjbGFzcz0iY2xzLTIiIGQ9Ik02NDYuOTQsNTc3LjQydi40NGwtMzQuMTcsMzYuOCwzNywzOC43OXYuODhINjMxLjg4bC0zMi4xOS0zNC4wNnYzNC4wNmgtMTQuNVY1NzcuNDJoMTQuNXYzMi41MmwyOS4yMy0zMi41MloiLz48cGF0aCBjbGFzcz0iY2xzLTEiIGQ9Ik02ODcuODEsNjIwLjgyaC0xMlY2MDloMTEuMWEyNywyNywwLDAsMCwxMS40Mi0yLjE0LDcuNTEsNy41MSwwLDAsMCw0LjczLTcuMDksOS4wNyw5LjA3LDAsMCwwLTMuNzQtNy4xNHEtMy43NC0zLjA4LTEyLjA4LTMuMDhhMzAuOTIsMzAuOTIsMCwwLDAtNy4yLjgzLDE0Ljc3LDE0Ljc3LDAsMCwwLTYuMTUsMy4wOCw2Ljk0LDYuOTQsMCwwLDAtMi42OSw1LjU0SDY1Ni43MWExOC44MSwxOC44MSwwLDAsMSw0Ljg0LTEzLjEzQTI1LjY1LDI1LjY1LDAsMCwxLDY3Myw1NzguNDFhNDcuNjYsNDcuNjYsMCwwLDEsMTQuMzQtMi4wOSwzNy4yNCwzNy4yNCwwLDAsMSwyMS4wOSw2cTksNiw5LjEyLDE3LjQxYTE2LjI2LDE2LjI2LDAsMCwxLTIuODYsOS41MSwxNC4zOCwxNC4zOCwwLDAsMS03LjgsNS42NSwxOC44NywxOC44NywwLDAsMSw5LjA3LDYuMjcsMTUuNiwxNS42LDAsMCwxLDMuNDYsOS44OCwyMy4xOCwyMy4xOCwwLDAsMS0yLDkuNzMsMTkuNzgsMTkuNzgsMCwwLDEtNS4xNiw3LDI3Ljg2LDI3Ljg2LDAsMCwxLTcuNTMsNC40NSw0MS4xMSw0MS4xMSwwLDAsMS04LjczLDIuNDcsNTYuNTQsNTYuNTQsMCwwLDEtOS4xMi43MSw0OC4zOCw0OC4zOCwwLDAsMS0xMS4xNi0xLjI2LDM3LDM3LDAsMCwxLTEwLTMuOTUsMjAuMzcsMjAuMzcsMCwwLDEtNy40Ny03LjQyLDIxLjExLDIxLjExLDAsMCwxLTIuODEtMTFoMTQuNjJxMCw1LjI4LDUuNDksOEEyNS43MSwyNS43MSwwLDAsMCw2ODcsNjQyLjQ2YTI0LjU2LDI0LjU2LDAsMCwwLDExLjgxLTIuODZxNS4zMi0yLjg1LDUuMzItOC40NlE3MDQuMTcsNjIwLjgyLDY4Ny44MSw2MjAuODJaIi8+PHBhdGggY2xhc3M9ImNscy0xIiBkPSJNNzQ3LjM1LDYwMS40OHYxMS4xYTE2LjI4LDE2LjI4LDAsMCwxLDcuNjQtNS45NCwyNi4xMSwyNi4xMSwwLDAsMSwxMC4xNi0yLjE5LDQ2LjM4LDQ2LjM4LDAsMCwxLDExLjU0LDEuMzcsMzYuODQsMzYuODQsMCwwLDEsMTAsNC4yMywyMC41NiwyMC41NiwwLDAsMSw3LjQxLDgsMjUuMDksMjUuMDksMCwwLDEsMi43NSwxMnEwLDEyLTkuNTYsMTguNzNhMzcuNjUsMzcuNjUsMCwwLDEtMjIuMyw2Ljc1LDQyLjEyLDQyLjEyLDAsMCwxLTE1LjMzLTIuNzQsMjguMTcsMjguMTcsMCwwLDEtMTEuOTItOC43NEEyMS45NCwyMS45NCwwLDAsMSw3MzMsNjI5LjgzVjYwMS4xNXEwLTEyLjA5LDkuMzQtMTguNjJUNzY0LjYsNTc2cTEzLjYyLDAsMjIuNTgsNS42NnQ5LDE3LjA4aC0xNC41cTAtNC44My01LTcuNTJhMjQuNTksMjQuNTksMCwwLDAtMTEuNzYtMi42OSwyMC42MSwyMC42MSwwLDAsMC0xMi4yLDMuNDZBMTEsMTEsMCwwLDAsNzQ3LjM1LDYwMS40OFpNNzY1LDY0Mi41N2EyMi41MiwyMi41MiwwLDAsMCwxMi40MS0zLjI0LDEwLjQsMTAuNCwwLDAsMCw1LjA2LTkuMjgsMTEsMTEsMCwwLDAtNC44OS05LjU2cS00Ljg5LTMuNC0xMi40Ny0zLjQxdC0xMi44LDMuNDFhMTEuMywxMS4zLDAsMCwwLC4xMSwxOC44NFE3NTcuNDYsNjQyLjU3LDc2NSw2NDIuNTdaIi8+PHBhdGggY2xhc3M9ImNscy0xIiBkPSJNODA5LjMxLDYyMS44MVY2MDkuOTRhMzcuOTIsMzcuOTIsMCwwLDEsMi44Ni0xNS4wNSwyOS45MywyOS45MywwLDAsMSw3LjQ3LTEwLjgyLDM0LjE5LDM0LjE5LDAsMCwxLDIyLjc0LTguNTcsMzUuOSwzNS45LDAsMCwxLDEyLjE0LDIuMjUsMzIuNDksMzIuNDksMCwwLDEsMTAuNjEsNi4zMiwzMCwzMCwwLDAsMSw3LjQ3LDEwLjg3LDM4LjI3LDM4LjI3LDAsMCwxLDIuODUsMTV2MTEuODdhMzguMTgsMzguMTgsMCwwLDEtMi44NSwxNSwzMCwzMCwwLDAsMS03LjQ3LDEwLjg4QTMyLjY2LDMyLjY2LDAsMCwxLDg1NC41Miw2NTRhMzYuMTQsMzYuMTQsMCwwLDEtMTIuMTQsMi4yNSwzNC4zMiwzNC4zMiwwLDAsMS0yMi43NC04LjU3LDMwLDMwLDAsMCwxLTcuNDctMTAuODJBMzgsMzgsMCwwLDEsODA5LjMxLDYyMS44MVptMTQuNC0xMS44N3YxMS44N2EyNC44MSwyNC44MSwwLDAsMCwxLjU5LDkuMTEsMTguMTUsMTguMTUsMCwwLDAsNC4yMyw2LjYsMTkuMTIsMTkuMTIsMCwwLDAsNiwzLjksMTcuODksMTcuODksMCwwLDAsMTMuNzMsMCwxOS4yMywxOS4yMywwLDAsMCw2LTMuOSwxOC4xNSwxOC4xNSwwLDAsMCw0LjIzLTYuNiwyNC44MSwyNC44MSwwLDAsMCwxLjU5LTkuMTFWNjA5Ljk0QTIzLjgsMjMuOCwwLDAsMCw4NTksNTk5LjgzYTE2Ljg0LDE2Ljg0LDAsMCwwLTUuMzItNi44MSwyMywyMywwLDAsMC03LjI2LTMuNDEsMTUsMTUsMCwwLDAtOCwwLDIzLjQ4LDIzLjQ4LDAsMCwwLTcuMjUsMy4zNiwxNi4zLDE2LjMsMCwwLDAtNS4zMyw2Ljc1QTI0LjA4LDI0LjA4LDAsMCwwLDgyMy43MSw2MDkuOTRaIi8+PC9zdmc+" alt="CoreTrak" style={{ height: 28, width: 'auto' }} />
        </div>
      </div>

      {/* ── Bottom pharmacy message bar ── */}
      {!bottomDismissed && (
        <div
          style={{
            position: 'fixed',
            bottom: 72,
            left: 12,
            right: 12,
            zIndex: 50,
            background: '#007C86',
            color: '#fff',
            borderRadius: 14,
            padding: '10px 14px',
            boxShadow: '0 4px 20px rgba(0,0,0,0.15)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 8,
          }}
        >
          <div style={{ fontSize: 12, letterSpacing: -0.1, flex: 1 }}>
            <span style={{ fontWeight: 700 }}>PHARMACY DEMO</span>
            {' — '}
            <span style={{ opacity: 0.85 }}>Medication creates weight loss. Habits protect muscle, health, and long-term results.</span>
          </div>
          <button
            type="button"
            onClick={handleDismissBottom}
            aria-label="Dismiss"
            style={{
              background: 'none',
              border: 'none',
              color: 'rgba(255,255,255,0.7)',
              fontSize: 18,
              cursor: 'pointer',
              padding: '0 4px',
              lineHeight: 1,
              flexShrink: 0,
              WebkitTapHighlightColor: 'transparent',
            }}
          >
            ×
          </button>
        </div>
      )}

      {/* ── Tier change toast ── */}
      <TierChangeToast tier={tier} />
    </>
  );
}

// ── Tier change toast ─────────────────────────────────────────────────────────

/**
 * Brief toast notification when the tier changes.
 * Appears for 2.5 seconds then fades out.
 */
function TierChangeToast({ tier }: { tier: string }) {
  const [visible, setVisible] = useState(false);
  const [lastTier, setLastTier] = useState(tier);

  if (tier !== lastTier) {
    setLastTier(tier);
    setVisible(true);
    setTimeout(() => setVisible(false), 2500);
  }

  if (!visible) return null;

  const isCore = tier === 'core';

  return (
    <div
      style={{
        position: 'fixed',
        top: 60,
        left: '50%',
        transform: 'translateX(-50%)',
        zIndex: 210,
        background: '#0f1729',
        border: `1px solid ${isCore ? 'rgba(0,124,134,0.3)' : 'rgba(0,168,181,0.4)'}`,
        borderRadius: 12,
        padding: '10px 18px',
        fontSize: 12,
        fontWeight: 600,
        color: '#fff',
        whiteSpace: 'nowrap' as const,
        boxShadow: '0 4px 20px rgba(0,0,0,0.25)',
        display: 'flex',
        alignItems: 'center',
        gap: 8,
        animation: 'fadeInDown 0.2s ease',
      }}
    >
      <span
        style={{
          width: 8,
          height: 8,
          borderRadius: '50%',
          background: isCore ? '#007C86' : '#00a8b5',
          flexShrink: 0,
        }}
      />
      Now showing{' '}
      <strong style={{ color: isCore ? '#007C86' : '#00a8b5' }}>
        CoreTrak {isCore ? 'Core (Free)' : 'Trak (Premium)'}
      </strong>
    </div>
  );
}
